import { useState, useEffect } from 'react';
import {
    ChevronDown, ChevronUp, Plus, Trash2, X,
    FolderOpen, Edit2, Check, ListPlus,
} from 'lucide-react';
import UnfoldMoreIcon from "@mui/icons-material/UnfoldMore";
import UnfoldLessIcon from "@mui/icons-material/UnfoldLess";
import ShowChartIcon from "@mui/icons-material/ShowChart";
import {
    getAllWatchlists,
    getWatchlistById,
    addStockToWatchlist,
    removeStockFromWatchlist,
    updateWatchlist,
    deleteWatchlist,
    createWatchlist,
    bulkAddToWatchlist
} from "../../../api/watchlist_api";
import { searchStockName } from "../../../api/api";
import {
    Box, Button, Typography, Dialog, DialogTitle,
    DialogContent, DialogActions,
    Radio, RadioGroup, FormControlLabel, FormControl,
    Alert,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import CreatePortfolioFromWatchlist from './createPortfolioFromWatchlist';
import { getCurrencySymbol } from "../../../utils/currency";
import { formatCurrency } from "../../../utils/currency";
const market = sessionStorage.getItem("selectedMarket") ?? "US";
const STOCKS_GRID = "100px 1.4fr 150px 120px 180px 120px 1fr 48px";

const EmptyWatchlists = ({ onCreate }) => (
    <Box
        sx={{
            bgcolor: 'var(--color-surface)',
            border: '1px solid var(--color-border-primary)',
            borderRadius: 1.5,
            py: 6,
            px: 3,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            textAlign: 'center'
        }}
    >
        <Box
            sx={{
                width: 48,
                height: 48,
                borderRadius: 1.5,
                bgcolor: 'var(--color-surface-hover)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                mb: 2
            }}
        >
            <FolderOpen style={{ color: 'var(--color-text-secondary)' }} size={24} />
        </Box>
        <Typography fontSize="var(--font-size-base)" fontWeight={600} color="var(--color-text-primary)" mb={1}>
            No watchlists yet
        </Typography>
        <Typography fontSize="var(--font-size-base)" color="var(--color-text-secondary)" maxWidth={420} mb={3} lineHeight={1.6}>
            Create your first watchlist to track stocks and monitor prices in real-time.
        </Typography>
        <Button
            onClick={onCreate}
            startIcon={<Plus size={16} />}
            sx={{
                bgcolor: 'var(--color-primary)',
                color: '#ffffff',
                fontSize: "var(--font-size-sm)",
                fontWeight: 500,
                textTransform: 'none',
                px: 2.5,
                py: 1,
                borderRadius: 1,
                '&:hover': {
                    bgcolor: 'var(--color-primary-dark)',
                }
            }}
        >
            Create watchlist
        </Button>
    </Box>
);

const WatchlistRow = ({ row, expanded, onToggle, onAdd, onRemoveStock, onReload, onCreatePortfolio }) => {
    const navigate = useNavigate();
    const [editing, setEditing] = useState(false);
    const [name, setName] = useState(row.name);
    const [saving, setSaving] = useState(false);
    const [confirmDelete, setConfirmDelete] = useState(false);
    const [deleting, setDeleting] = useState(false);
    const [confirmDeleteStock, setConfirmDeleteStock] = useState(false);
    const [stockToDelete, setStockToDelete] = useState(null);
    const [deletingStock, setDeletingStock] = useState(false);
    const stocks = row.stocks || [];
    const stockCount = row.stock_count ?? stocks.length;
    const sym = getCurrencySymbol();

    const handleSave = async () => {
        if (!name.trim() || name === row.name) {
            setEditing(false);
            setName(row.name);
            return;
        }
        setSaving(true);
        await updateWatchlist(row.id, { name: name.trim(), is_default: row.is_default });
        setSaving(false);
        onReload();
        setEditing(false);
    };

    const handleDelete = async () => {
        setDeleting(true);
        await deleteWatchlist(row.id);
        setDeleting(false);
        setConfirmDelete(false);
        onReload();
    };

    const buttonStyle = {
        p: 0.5,
        color: 'var(--color-text-secondary)',
        bgcolor: 'transparent',
        border: 'none',
        borderRadius: 0.5,
        cursor: 'pointer',
        minWidth: 'auto',
        '&:hover': {
            bgcolor: 'var(--color-surface-hover)',
        }
    };

    return (
        <>
            <tr style={{
                backgroundColor: 'var(--color-surface)',
                borderBottom: '1px solid var(--color-border-primary)',
            }}>
                <td style={{ padding: '12px 8px' }}>
                    <Box display="flex" alignItems="center" gap={1}>
                        <Button onClick={() => onToggle(row.id)} sx={buttonStyle}>
                            {expanded ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
                        </Button>

                        {editing ? (
                            <input
                                autoFocus
                                value={name}
                                onChange={(e) => setName(e.target.value)}
                                onKeyDown={(e) => {
                                    if (e.key === "Enter") handleSave();
                                    if (e.key === "Escape") { setEditing(false); setName(row.name); }
                                }}
                                style={{
                                    padding: '4px 8px',
                                    backgroundColor: 'var(--color-bg-secondary)',
                                    border: '1px solid var(--color-border-primary)',
                                    color: 'var(--color-text-primary)',
                                    borderRadius: '4px',
                                    fontSize: '13.5px',
                                    outline: 'revert',
                                    width: '192px',
                                    fontFamily: 'inherit'
                                }}
                            />
                        ) : (
                            <Typography fontSize="var(--font-size-base)" fontWeight={600} color="var(--color-text-primary)">
                                {row.name}
                            </Typography>
                        )}

                        {!editing && (
                            <Button onClick={() => setEditing(true)} sx={buttonStyle}>
                                <Edit2 size={16} />
                            </Button>
                        )}
                        {editing && (
                            <>
                                <Button onClick={handleSave} disabled={saving} sx={{ ...buttonStyle, color: 'var(--color-success)' }}>
                                    {saving ? <Box sx={{ width: 14, height: 14, border: 2, borderColor: 'var(--color-success)', borderTopColor: 'transparent', borderRadius: '50%', animation: 'spin 0.6s linear infinite' }} /> : <Check size={16} />}
                                </Button>
                                <Button onClick={() => { setEditing(false); setName(row.name); }} sx={buttonStyle}>
                                    <X size={16} />
                                </Button>
                            </>
                        )}
                        <Button onClick={() => setConfirmDelete(true)} sx={{ ...buttonStyle, '&:hover': { bgcolor: 'var(--color-surface-hover)', color: 'var(--color-error)' } }}>
                            <Trash2 size={16} />
                        </Button>
                    </Box>
                </td>
                <td style={{ padding: '12px 8px' }}>
                    <Box component="span" sx={{ px: 1, py: 0.5, bgcolor: 'var(--color-surface-hover)', color: 'var(--color-text-primary)', fontSize: "var(--font-size-sm)", fontWeight: 500, borderRadius: 0.5 }}>
                        {stockCount}
                    </Box>
                </td>

                <td style={{ padding: '12px 8px' }}>
                    <Button
                        disabled={market === "ALL"}
                        onClick={() => onAdd(row.id)}
                        sx={{
                            fontSize: "var(--font-size-sm)",
                            color: 'var(--color-primary)',
                            fontWeight: 700,
                            textTransform: 'none',
                            minWidth: 'auto',
                            p: 0,
                            '&:hover': {
                                bgcolor: 'transparent',
                                textDecoration: 'underline'
                            }
                        }}
                    >
                        + Add symbols
                    </Button>
                </td>
                {market !== "ALL" && (
                    <td style={{ padding: '12px 8px', textAlign: 'right' }}>
                        <Button
                            onClick={() => onCreatePortfolio(row)}
                            disabled={stockCount === 0}
                            sx={{
                                bgcolor: stockCount > 0 ? "var(--color-success)" : "var(--color-surface-hover)",
                                color: "#ffffff",
                                fontSize: "var(--font-size-sm)",
                                fontWeight: "var(--font-weight-semibold)",
                                textTransform: "none",
                                px: 1.5,
                                py: 0.65,
                                borderRadius: 1,
                                boxShadow: "none",
                                "&:hover": {
                                    bgcolor: stockCount > 0 ? "var(--color-success)" : "var(--color-surface-hover)",
                                    opacity: stockCount > 0 ? 0.9 : 1,
                                    boxShadow: "none",
                                },
                                "&.Mui-disabled": {
                                    bgcolor: "var(--color-surface-hover)",
                                    color: "var(--color-text-muted)",
                                },
                            }}
                        >
                            Create Portfolio
                        </Button>
                    </td>
                )}
            </tr >

            {expanded && (
                <tr>
                    <td colSpan={4} style={{ padding: 0 }}>
                        <Box sx={{ bgcolor: 'var(--color-bg-secondary)', px: 1.5, py: 1.5 }}>
                            {stocks.length === 0 ? (
                                <Box py={3} textAlign="center">
                                    <Typography fontSize="var(--font-size-base)" color="var(--color-text-secondary)" fontWeight={500}>
                                        No stocks in this watchlist
                                    </Typography>
                                </Box>
                            ) : (
                                <>
                                    <Box
                                        sx={{
                                            display: 'grid',
                                            gridTemplateColumns: STOCKS_GRID,
                                            alignItems: 'center',
                                            fontSize: 15,
                                            fontWeight: 700,
                                            color: 'var(--color-text-primary)',
                                            textTransform: 'uppercase',
                                            letterSpacing: '0.05em',
                                            py: 1,
                                            px: 1,
                                            borderBottom: '1px solid var(--color-border-primary)'
                                        }}
                                    >
                                        <span>Ticker</span>
                                        <span>Company</span>
                                        <span>Sector</span>
                                        <span>Price</span>
                                        <span>Market Cap</span>
                                        <span>Added On</span>
                                        {/* <span>Notes</span> */}
                                        <span></span>
                                    </Box>
                                    {stocks.map((s) => (
                                        <Box
                                            key={s.ticker}
                                            sx={{
                                                display: 'grid',
                                                gridTemplateColumns: STOCKS_GRID,
                                                alignItems: 'center',
                                                fontSize: "var(--font-size-sm)",
                                                py: 1,
                                                px: 1,
                                                borderBottom: '1px solid var(--color-border-primary)',
                                                '&:hover': {
                                                    bgcolor: 'var(--color-surface)'
                                                }
                                            }}
                                        >
                                            <Typography
                                                fontSize="var(--font-size-sm)"
                                                fontWeight={700}
                                                color="var(--color-primary)"
                                                sx={{
                                                    cursor: 'pointer',
                                                    '&:hover': { textDecoration: 'underline' }
                                                }}
                                                onClick={() => navigate(`/stocks/${s.ticker}/details`)}
                                            >
                                                {s.ticker}
                                            </Typography>
                                            <Typography fontSize="var(--font-size-sm)" color="var(--color-text-secondary)" noWrap>
                                                {s.company_name}
                                            </Typography>
                                            <Typography fontSize="var(--font-size-sm)" color="var(--color-text-secondary)">
                                                {s.sector_name ?? "—"}
                                            </Typography>
                                            <Typography fontSize="var(--font-size-sm)" color="var(--color-text-primary)">
                                                {/* {s.current_price ? `$${Number(s.current_price).toFixed(2)}` : "—"} */}
                                                {s.current_price ? `${sym}${Number(s.current_price).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : "—"}
                                            </Typography>
                                            <Typography fontSize="var(--font-size-sm)" color="var(--color-text-primary)">
                                                {s.market_cap ? formatCurrency(s.market_cap, { compact: true }) : "—"}
                                            </Typography>
                                            <Typography fontSize="var(--font-size-sm)" color="var(--color-text-secondary)">
                                                {s.added_at ? new Date(s.added_at).toLocaleDateString() : "—"}
                                            </Typography>
                                            <Typography fontSize="var(--font-size-sm)" color="var(--color-text-secondary)" noWrap>
                                                {/* {s.notes || "—"} */}
                                            </Typography>
                                            <Button
                                                onClick={() => {
                                                    setStockToDelete(s);
                                                    setConfirmDeleteStock(true);
                                                }}
                                                sx={{
                                                    p: 0.5,
                                                    minWidth: 'auto',
                                                    color: 'var(--color-text-secondary)',
                                                    '&:hover': {
                                                        bgcolor: 'var(--color-error-light)',
                                                        color: 'var(--color-error)'
                                                    }
                                                }}
                                            >
                                                <Trash2 size={15} />
                                            </Button>
                                        </Box>
                                    ))}
                                </>
                            )}
                        </Box>
                    </td>
                </tr>
            )
            }

            {/* Delete Watchlist Dialog */}
            <Dialog
                open={confirmDelete}
                onClose={() => setConfirmDelete(false)}
                maxWidth="xs"
                fullWidth
                PaperProps={{
                    sx: {
                        bgcolor: "var(--color-surface)",
                        backgroundImage: "none",
                        borderRadius: 1.5,
                        border: "1px solid var(--color-border-primary)",
                    },
                }}
            >
                <DialogTitle sx={{ color: "var(--color-text-primary)", borderBottom: "1px solid var(--color-border-primary)", py: 1.5, fontSize: 16, fontWeight: 600 }}>
                    Delete watchlist
                </DialogTitle>

                <DialogContent sx={{ pt: 2, pb: 1.5 }}>
                    <Typography fontSize="var(--font-size-base)" color="var(--color-text-secondary)" lineHeight={1.6} my={2}>
                        Are you sure you want to delete{" "}
                        <Typography component="span" fontWeight={600} color="var(--color-text-primary)">
                            {row.name}
                        </Typography>
                        ? This action cannot be undone.
                    </Typography>
                </DialogContent>

                <DialogActions sx={{ px: 2.5, pb: 1.5, borderTop: "1px solid var(--color-border-primary)" }}>
                    <Button
                        onClick={() => setConfirmDelete(false)}
                        disabled={deleting}
                        sx={{
                            textTransform: "none",
                            fontSize: "var(--font-size-sm)",
                            fontWeight: 500,
                            color: "var(--color-text-secondary)",
                            "&:hover": { bgcolor: "var(--color-surface-hover)" },
                        }}
                    >
                        Cancel
                    </Button>

                    <Button
                        variant="contained"
                        disabled={deleting}
                        onClick={handleDelete}
                        sx={{
                            textTransform: "none",
                            fontSize: "var(--font-size-sm)",
                            fontWeight: 500,
                            bgcolor: "var(--color-error)",
                            "&:hover": { bgcolor: "var(--color-error)", opacity: 0.9 },
                            boxShadow: "none",
                            py: 0.5,
                            px: 1.75,
                        }}
                    >
                        Delete
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Delete Stock Dialog */}
            <Dialog
                open={confirmDeleteStock}
                onClose={() => setConfirmDeleteStock(false)}
                maxWidth="xs"
                fullWidth
                PaperProps={{
                    sx: {
                        bgcolor: "var(--color-surface)",
                        backgroundImage: "none",
                        borderRadius: 1.5,
                        border: "1px solid var(--color-border-primary)",
                    },
                }}
            >
                <DialogTitle sx={{ color: "var(--color-text-primary)", borderBottom: "1px solid var(--color-border-primary)", py: 1.5, fontSize: 16, fontWeight: 600 }}>
                    Remove stock
                </DialogTitle>

                <DialogContent sx={{ pt: 2, pb: 1.5 }}>
                    <Typography fontSize="var(--font-size-base)" color="var(--color-text-secondary)" lineHeight={1.6}>
                        Are you sure you want to remove{" "}
                        <Typography component="span" fontWeight={600} color="var(--color-text-primary)">
                            {stockToDelete?.ticker}
                        </Typography>
                        {stockToDelete?.company_name && (
                            <>
                                {" "}
                                <Typography component="span" color="var(--color-text-secondary)">
                                    ({stockToDelete.company_name})
                                </Typography>
                            </>
                        )}
                        {" "}from{" "}
                        <Typography component="span" fontWeight={600} color="var(--color-text-primary)">
                            {row.name}
                        </Typography>
                        ?
                    </Typography>
                </DialogContent>

                <DialogActions sx={{ px: 2.5, pb: 1.5, borderTop: "1px solid var(--color-border-primary)" }}>
                    <Button
                        onClick={() => {
                            setConfirmDeleteStock(false);
                            setStockToDelete(null);
                        }}
                        disabled={deletingStock}
                        sx={{
                            textTransform: "none",
                            fontSize: "var(--font-size-sm)",
                            fontWeight: 500,
                            color: "var(--color-text-secondary)",
                            "&:hover": { bgcolor: "var(--color-surface-hover)" },
                        }}
                    >
                        Cancel
                    </Button>

                    <Button
                        variant="contained"
                        disabled={deletingStock}
                        onClick={async () => {
                            setDeletingStock(true);
                            await onRemoveStock(row.id, stockToDelete.ticker);
                            setDeletingStock(false);
                            setConfirmDeleteStock(false);
                            setStockToDelete(null);
                        }}
                        sx={{
                            textTransform: "none",
                            fontSize: "var(--font-size-sm)",
                            fontWeight: 500,
                            bgcolor: "var(--color-error)",
                            "&:hover": { bgcolor: "var(--color-error)", opacity: 0.9 },
                            boxShadow: "none",
                            py: 0.5,
                            px: 1.75,
                        }}
                    >
                        Remove
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    );
};

// Efficient Frontier Dialog Component
const EfficientFrontierDialog = ({ open, onClose, watchlists, onNavigate }) => {
    const [selectedWatchlistId, setSelectedWatchlistId] = useState("");

    const handleProceed = () => {
        if (!selectedWatchlistId) return;
        onNavigate(selectedWatchlistId);
    };

    const handleClose = () => {
        setSelectedWatchlistId("");
        onClose();
    };

    return (
        <Dialog
            open={open}
            onClose={handleClose}
            maxWidth="sm"
            fullWidth
            PaperProps={{
                sx: {
                    bgcolor: "var(--color-surface)",
                    backgroundImage: "none",
                    borderRadius: 1.5,
                    border: "1px solid var(--color-border-primary)",
                },
            }}
        >
            <DialogTitle
                sx={{
                    color: "var(--color-text-primary)",
                    borderBottom: "1px solid var(--color-border-primary)",
                    py: 2,
                    px: 3,
                    display: "flex",
                    alignItems: "center",
                    gap: 1.5,
                }}
            >
                <Box
                    sx={{
                        width: 36,
                        height: 36,
                        borderRadius: 1,
                        bgcolor: "var(--color-primary)",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                    }}
                >
                    <ShowChartIcon sx={{ fontSize: 18, color: "#fff" }} />
                </Box>
                <Typography fontSize="var(--font-size-base)" fontWeight={600}>
                    Check Efficient Frontier
                </Typography>
            </DialogTitle>

            <DialogContent sx={{ pt: 3, pb: 2, px: 3 }}>
                <Typography fontSize="var(--font-size-base)" color="var(--color-text-secondary)" lineHeight={1.6} my={2}>
                    Select a watchlist to analyze its efficient frontier and optimize portfolio allocation.
                </Typography>

                <FormControl component="fieldset" fullWidth>
                    <RadioGroup
                        value={selectedWatchlistId}
                        onChange={(e) => setSelectedWatchlistId(e.target.value)}
                    >
                        {watchlists.map((watchlist) => {
                            const stockCount = watchlist.stock_count ?? watchlist.stocks?.length ?? 0;
                            const isDisabled = stockCount < 2;

                            return (
                                <Box
                                    key={watchlist.id}
                                    sx={{
                                        mb: 1.5,
                                        p: 1.5,
                                        borderRadius: 1,
                                        border: "1px solid var(--color-border-primary)",
                                        bgcolor: selectedWatchlistId === watchlist.id ? "var(--color-surface-hover)" : "var(--color-bg-secondary)",
                                        opacity: isDisabled ? 0.5 : 1,
                                        cursor: isDisabled ? "not-allowed" : "pointer",
                                        "&:hover": {
                                            bgcolor: isDisabled ? "var(--color-bg-secondary)" : "var(--color-surface-hover)",
                                        },
                                    }}
                                    onClick={() => !isDisabled && setSelectedWatchlistId(watchlist.id)}
                                >
                                    <FormControlLabel
                                        value={watchlist.id}
                                        control={
                                            <Radio
                                                disabled={isDisabled}
                                                sx={{
                                                    color: "var(--color-text-muted)",
                                                    "&.Mui-checked": {
                                                        color: "var(--color-primary)",
                                                    },
                                                    "&.Mui-disabled": {
                                                        color: "var(--color-text-muted)",
                                                    },
                                                }}
                                            />
                                        }
                                        label={
                                            <Box display="flex" alignItems="center" gap={1.5} width="100%">
                                                <Box flex={1}>
                                                    <Typography fontSize="var(--font-size-base)" fontWeight={600} color="var(--color-text-primary)">
                                                        {watchlist.name}
                                                    </Typography>
                                                    <Typography fontSize="var(--font-size-sm)" color="var(--color-text-muted)">
                                                        {stockCount} {stockCount === 1 ? 'stock' : 'stocks'}
                                                        {isDisabled && ' (Need at least 2 stocks)'}
                                                    </Typography>
                                                </Box>
                                            </Box>
                                        }
                                        sx={{
                                            m: 0,
                                            width: "100%",
                                            pointerEvents: isDisabled ? "none" : "auto",
                                        }}
                                    />
                                </Box>
                            );
                        })}
                    </RadioGroup>
                </FormControl>

                {watchlists.every(w => (w.stock_count ?? w.stocks?.length ?? 0) < 2) && (
                    <Box
                        sx={{
                            mt: 2,
                            p: 2,
                            bgcolor: "var(--color-bg-secondary)",
                            border: "1px solid var(--color-border-primary)",
                            borderRadius: 1,
                        }}
                    >
                        <Typography fontSize="var(--font-size-sm)" color="var(--color-text-muted)" textAlign="center">
                            All watchlists need at least 2 stocks for frontier analysis.
                        </Typography>
                    </Box>
                )}
            </DialogContent>

            <DialogActions sx={{ px: 3, pb: 2.5, pt: 1, borderTop: "1px solid var(--color-border-primary)" }}>
                <Button
                    onClick={handleClose}
                    sx={{
                        textTransform: "none",
                        fontSize: "var(--font-size-sm)",
                        fontWeight: 500,
                        color: "var(--color-text-secondary)",
                        "&:hover": { bgcolor: "var(--color-surface-hover)" },
                    }}
                >
                    Cancel
                </Button>

                <Button
                    variant="contained"
                    disabled={!selectedWatchlistId}
                    onClick={handleProceed}
                    sx={{
                        textTransform: "none",
                        fontSize: "var(--font-size-sm)",
                        fontWeight: 600,
                        bgcolor: "var(--color-primary)",
                        "&:hover": { bgcolor: "var(--color-primary-dark)" },
                        boxShadow: "none",
                        py: 0.75,
                        px: 2,
                        "&.Mui-disabled": {
                            bgcolor: "var(--color-surface-hover)",
                            color: "var(--color-text-muted)",
                        },
                    }}
                >
                    Proceed
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default function Watchlist() {
    const navigate = useNavigate();
    const [watchlists, setWatchlists] = useState([]);
    const [expandedMap, setExpandedMap] = useState({});
    const [loading, setLoading] = useState(false);
    const [openCreate, setOpenCreate] = useState(false);
    const [newName, setNewName] = useState("");
    const [creating, setCreating] = useState(false);
    const [snackbar, setSnackbar] = useState(null);

    const [openAddSymbols, setOpenAddSymbols] = useState(false);
    const [activeWatchlistId, setActiveWatchlistId] = useState(null);
    const [searchQuery, setSearchQuery] = useState("");
    const [searchResults, setSearchResults] = useState([]);
    const [searching, setSearching] = useState(false);
    const [selectedSymbols, setSelectedSymbols] = useState([]);

    const [openEfficientFrontier, setOpenEfficientFrontier] = useState(false);
    const [openCreateFromWatchlist, setOpenCreateFromWatchlist] = useState(false);
    const [selectedWatchlist, setSelectedWatchlist] = useState(null);

    const handleCreatePortfolio = (watchlist) => {
        setSelectedWatchlist(watchlist);
        setOpenCreateFromWatchlist(true);
    };

    useEffect(() => {
        fetchWatchlists();
    }, []);

    useEffect(() => {
        if (snackbar) {
            const timer = setTimeout(() => setSnackbar(null), 4000);
            return () => clearTimeout(timer);
        }
    }, [snackbar]);

    const fetchWatchlists = async () => {
        setLoading(true);
        const res = await getAllWatchlists();

        if (!res?.success) {
            setLoading(false);
            return;
        }

        const allWatchlists = res.data || [];

        // ✅ Show the list immediately — stock_count is already in the list API response
        setWatchlists(allWatchlists);
        setLoading(false);

        // 🔄 Fetch individual details in the background, patch each row as it arrives
        allWatchlists.forEach(async (watchlist) => {
            const detailRes = await getWatchlistById(watchlist.id);
            if (!detailRes?.success) return;

            setWatchlists((prev) =>
                prev.map((w) =>
                    w.id !== watchlist.id
                        ? w
                        : {
                            ...w,
                            ...detailRes.data,
                            // Preserve list-level stock_count in case detail API omits it
                            stock_count: detailRes.data.stock_count ?? w.stock_count,
                        }
                )
            );
        });
    };

    const reloadWatchlistById = async (id) => {
        const res = await getWatchlistById(id);
        if (res?.success) {
            setWatchlists(prev => prev.map(w => w.id === id ? res.data : w));
        }
    };

    const handleExpandAll = async () => {
        const newExpandedMap = {};
        watchlists.forEach(w => {
            newExpandedMap[w.id] = true;
        });
        setExpandedMap(newExpandedMap);

        const fetchPromises = watchlists
            .filter(w => !w.stocks || w.stocks.length === 0)
            .map(w => reloadWatchlistById(w.id));

        await Promise.all(fetchPromises);
    };

    const handleCollapseAll = () => {
        setExpandedMap({});
    };

    const handleCreateWatchlist = async () => {
        if (!newName.trim()) return;

        setCreating(true);
        const res = await createWatchlist({ name: newName.trim() });
        setCreating(false);
        if (res?.success) {
            setOpenCreate(false);
            setNewName("");
            fetchWatchlists();
            setSnackbar({ message: "Watchlist created" });
        }
    };

    const removeStock = async (watchlistId, ticker) => {
        await removeStockFromWatchlist(watchlistId, ticker);
        await reloadWatchlistById(watchlistId);
        setSnackbar({ message: `${ticker} removed` });
    };

    const handleAddSymbols = (watchlistId) => {
        setActiveWatchlistId(watchlistId);
        setOpenAddSymbols(true);
    };

    const handleSaveSymbols = async () => {
        if (selectedSymbols.length === 0) return;

        if (selectedSymbols.length === 1) {
            await addStockToWatchlist(activeWatchlistId, { ticker: selectedSymbols[0] });
        } else {
            await bulkAddToWatchlist(
                activeWatchlistId,
                selectedSymbols.map((ticker) => ({ ticker }))
            );
        }

        setOpenAddSymbols(false);
        setSearchQuery("");
        setSearchResults([]);
        setSelectedSymbols([]);
        await reloadWatchlistById(activeWatchlistId);
        setSnackbar({ message: `${selectedSymbols.length} symbol(s) added` });
    };

    const handleSearch = async (query) => {
        setSearchQuery(query);
        if (!query.trim()) {
            setSearchResults([]);
            return;
        }

        setSearching(true);
        try {
            const res = await searchStockName(query.trim());
            if (res?.success) {
                setSearchResults(res.data || []);
            } else {
                setSearchResults([]);
            }
        } catch (error) {
            console.error('Search error:', error);
            setSearchResults([]);
        }
        setSearching(false);
    };

    const handleNavigateToFrontier = (watchlistId) => {
        setOpenEfficientFrontier(false);
        navigate(`/frontierSimulator?watchlistId=${watchlistId}`);
    };

    return (
        <Box
            sx={{
                minHeight: "100vh",
                bgcolor: "var(--color-bg-primary)",
                px: { xs: 2, md: 4 },
                py: 3.5,
            }}
        >
            {/* Header */}
            <Box
                display="flex"
                justifyContent="space-between"
                alignItems="center"
                flexWrap="wrap"
                mb={2.5}
                gap={2}
            >
                <Box style={{ display: "flex", flexDirection: "column" }}>
                    <Typography
                        variant='title'
                        fontWeight="var(--font-weight-bold)"
                        className="text-primary"
                        sx={{ color: 'var(--color-text-primary)' }}
                        mb={0.25}
                        letterSpacing="-0.01em"
                    >
                        Watchlist
                    </Typography>
                    <Typography
                        variant='subtitle'
                        sx={{ color: 'var(--color-text-secondary)' }}
                        fontWeight={400}
                    >
                        Track and monitor your favorite stocks in real-time.
                    </Typography>
                </Box>

                <Box
                    sx={{
                        bgcolor: "var(--color-surface)",
                        border: "1px solid var(--color-border-primary)",
                        borderRadius: 1,
                        px: 2.5,
                        py: 1.25,
                        minWidth: 200,
                        textAlign: "right",
                    }}
                >
                    <Typography
                        fontSize="var(--font-size-base)"
                        fontWeight={600}
                        color="var(--color-text-secondary)"
                        letterSpacing="0.08em"
                        textTransform="uppercase"
                        mb={0.25}
                    >
                        Total Watchlists
                    </Typography>
                    <Typography fontSize={22} fontWeight={700} color="var(--color-success)">
                        {watchlists.length}
                    </Typography>
                </Box>
            </Box>

            {/* Action Bar */}
            <Box
                display="flex"
                justifyContent="space-between"
                alignItems="center"
                mb={2}
                flexWrap="wrap"
                gap={1.5}
            >
                <Box display="flex" gap={1} flexWrap="wrap">
                    <Button
                        startIcon={<UnfoldMoreIcon sx={{ fontSize: 18 }} />}
                        onClick={handleExpandAll}
                        disabled={loading}
                        sx={{
                            bgcolor: "var(--color-surface)",
                            color: "var(--color-text-primary)",
                            fontSize: 15,
                            fontWeight: 600,
                            textTransform: "none",
                            px: 1.75,
                            py: 0.5,
                            borderRadius: 1,
                            border: "1px solid var(--color-border-primary)",
                            "&:hover": {
                                bgcolor: "var(--color-surface-hover)",
                            },
                            "&.Mui-disabled": {
                                color: "var(--color-text-muted)",
                                borderColor: "var(--color-border-primary)",
                            },
                        }}
                    >
                        Expand All
                    </Button>

                    <Button
                        startIcon={<UnfoldLessIcon sx={{ fontSize: 18 }} />}
                        onClick={handleCollapseAll}
                        disabled={loading}
                        sx={{
                            bgcolor: "var(--color-surface)",
                            color: "var(--color-text-primary)",
                            fontSize: 15,
                            fontWeight: 600,
                            textTransform: "none",
                            px: 1.75,
                            py: 0.5,
                            borderRadius: 1,
                            border: "1px solid var(--color-border-primary)",
                            "&:hover": {
                                bgcolor: "var(--color-surface-hover)",
                            },
                            "&.Mui-disabled": {
                                color: "var(--color-text-muted)",
                                borderColor: "var(--color-border-primary)",
                            },
                        }}
                    >
                        Collapse All
                    </Button>

                    <Button
                        startIcon={<ShowChartIcon sx={{ fontSize: 18 }} />}
                        onClick={() => setOpenEfficientFrontier(true)}
                        disabled={loading || watchlists.length === 0 || market === "ALL"}
                        sx={{
                            bgcolor: "var(--color-surface)",
                            color: "var(--color-text-primary)",
                            fontSize: 15,
                            fontWeight: 600,
                            textTransform: "none",
                            px: 1.75,
                            py: 0.5,
                            borderRadius: 1,
                            border: "1px solid var(--color-border-primary)",
                            "&:hover": {
                                bgcolor: "var(--color-surface-hover)",
                            },
                        }}
                    >
                        Check Efficient Frontier
                    </Button>
                </Box>

                <Box display="flex" gap={1.5} alignItems="center" flexWrap="wrap">
                    {market !== "ALL" &&
                        <Button
                            startIcon={<Plus size={18} />}
                            onClick={() => setOpenCreate(true)}
                            sx={{
                                bgcolor: "var(--color-primary)",
                                color: "#fff",
                                fontSize: 15,
                                fontWeight: 600,
                                textTransform: "none",
                                px: 2,
                                py: 0.75,
                                borderRadius: 1,
                                "&:hover": {
                                    bgcolor: "var(--color-primary-dark)",
                                },
                            }}
                        >
                            New Watchlist
                        </Button>
                    }
                </Box>
            </Box>

            {/* Main Content */}
            <Box display="flex" gap={2}>
                <Box flex={1}>
                    {market === "ALL" &&
                        <Alert
                            severity="info"
                            sx={{
                                mb: 2,
                                bgcolor: "var(--color-info-light)",
                                color: "var(--color-text-primary)",
                                border: "1px solid var(--color-info)",
                                borderRadius: 1,
                                fontSize: "var(--font-size-sm)",
                                fontWeight: "var(--font-weight-medium)",
                                "& .MuiAlert-icon": {
                                    color: "var(--color-info)",
                                },
                            }}
                        >
                            Select a specific market to add or edit ticker(s) to your watchlist.
                        </Alert>
                    }
                    <Box
                        sx={{
                            bgcolor: 'var(--color-surface)',
                            border: '1px solid var(--color-border-primary)',
                            borderRadius: 1.5,
                            overflow: 'hidden'
                        }}
                    >
                        {loading ? (
                            <Box py={6} textAlign="center">
                                <Box
                                    sx={{
                                        width: 28,
                                        height: 28,
                                        border: 4,
                                        borderColor: 'var(--color-primary)',
                                        borderTopColor: 'transparent',
                                        borderRadius: '50%',
                                        animation: 'spin 0.6s linear infinite',
                                        margin: '0 auto'
                                    }}
                                />
                            </Box>
                        ) : watchlists.length === 0 ? (
                            <EmptyWatchlists onCreate={() => setOpenCreate(true)} />
                        ) : (
                            <table style={{ width: '100%', fontSize: 'var(--font-size-sm)', borderCollapse: 'collapse' }}>
                                <thead style={{ backgroundColor: 'var(--color-surface-hover)' }}>
                                    <tr style={{ borderBottom: '1px solid var(--color-border-primary)' }}>
                                        {["Watchlist", "Stocks", "Add", ...(market !== "ALL" ? ["Actions"] : [])].map((h) => (
                                            <th key={h} style={{
                                                padding: '10px 8px',
                                                textAlign: 'left',
                                                fontSize: 'var(--font-size-base)',
                                                fontWeight: 'var(--font-weight-bold)',
                                                color: 'var(--color-text-primary)',
                                                textTransform: 'uppercase',
                                                letterSpacing: '0.05em'
                                            }}>
                                                {h}
                                            </th>
                                        ))}
                                    </tr>
                                </thead>
                                <tbody>
                                    {watchlists.map((w) => (
                                        <WatchlistRow
                                            key={w.id}
                                            row={w}
                                            market={market}
                                            expanded={!!expandedMap[w.id]}
                                            onToggle={(id) => setExpandedMap(p => ({ ...p, [id]: !p[id] }))}
                                            onAdd={handleAddSymbols}
                                            onRemoveStock={removeStock}
                                            onReload={fetchWatchlists}
                                            onCreatePortfolio={handleCreatePortfolio}
                                        />
                                    ))}
                                </tbody>
                            </table>
                        )}
                    </Box>
                </Box>
            </Box>

            {/* Create Watchlist Dialog */}
            <Dialog
                open={openCreate}
                onClose={() => setOpenCreate(false)}
                maxWidth="md"
                fullWidth
                PaperProps={{
                    sx: {
                        bgcolor: "var(--color-surface)",
                        backgroundImage: "none",
                        borderRadius: 1.5,
                        border: "1px solid var(--color-border-primary)",
                    },
                }}
            >
                <DialogTitle
                    sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        p: 2.5,
                        pb: 2,
                        borderBottom: "1px solid var(--color-border-primary)",
                    }}
                >
                    <Box display="flex" alignItems="center" gap={1.5}>
                        <Box
                            sx={{
                                width: 36,
                                height: 36,
                                borderRadius: 1,
                                bgcolor: "var(--color-primary-lighter)",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                            }}
                        >
                            <ListPlus style={{ color: "var(--color-primary)" }} size={18} />
                        </Box>
                        <Typography fontSize="var(--font-size-base)" fontWeight={600} color="var(--color-text-primary)">
                            Create Watchlist
                        </Typography>
                    </Box>
                    <Button
                        onClick={() => setOpenCreate(false)}
                        sx={{
                            p: 0.5,
                            minWidth: 'auto',
                            color: 'var(--color-text-secondary)',
                            '&:hover': { bgcolor: 'var(--color-surface-hover)' }
                        }}
                    >
                        <X size={20} />
                    </Button>
                </DialogTitle>
                <DialogContent sx={{ p: 2.5 }}>
                    <Typography fontSize="var(--font-size-base)" color="var(--color-text-secondary)" my={2} lineHeight={1.6}>
                        Create a new watchlist to track your favorite stocks and monitor their performance in real-time.
                    </Typography>
                    <input
                        autoFocus
                        type="text"
                        placeholder="e.g., Tech Stocks, Banking, Long Term"
                        value={newName}
                        onChange={(e) => setNewName(e.target.value)}
                        onKeyDown={(e) => e.key === "Enter" && newName.trim() && handleCreateWatchlist()}
                        style={{
                            width: '100%',
                            padding: '8px 16px',
                            backgroundColor: 'var(--color-bg-secondary)',
                            border: '1px solid var(--color-border-primary)',
                            color: 'var(--color-text-primary)',
                            borderRadius: '8px',
                            fontSize: '13.5px',
                            fontFamily: 'inherit'
                        }}
                    />
                    <DialogActions sx={{ gap: 1, mt: 2.5, p: 0 }}>
                        <Button
                            onClick={() => setOpenCreate(false)}
                            disabled={creating}
                            fullWidth
                            sx={{
                                textTransform: "none",
                                fontSize: "var(--font-size-sm)",
                                fontWeight: 500,
                                color: "var(--color-text-secondary)",
                                "&:hover": { bgcolor: "var(--color-surface-hover)" },
                            }}
                        >
                            Cancel
                        </Button>
                        <Button
                            onClick={handleCreateWatchlist}
                            disabled={!newName.trim() || creating}
                            fullWidth
                            variant="contained"
                            sx={{
                                textTransform: "none",
                                fontSize: "var(--font-size-sm)",
                                fontWeight: 500,
                                bgcolor: "var(--color-primary)",
                                "&:hover": { bgcolor: "var(--color-primary-dark)" },
                                boxShadow: "none",
                                "&.Mui-disabled": {
                                    bgcolor: "var(--color-surface-hover)",
                                    color: "var(--color-text-muted)"
                                }
                            }}
                        >
                            Create watchlist
                        </Button>
                    </DialogActions>
                </DialogContent>
            </Dialog>

            {/* Add Symbols Dialog */}
            <Dialog
                open={openAddSymbols}
                onClose={() => {
                    setOpenAddSymbols(false);
                    setSearchQuery("");
                    setSearchResults([]);
                    setSelectedSymbols([]);
                }}
                maxWidth="md"
                fullWidth
                PaperProps={{
                    sx: {
                        bgcolor: "var(--color-surface)",
                        backgroundImage: "none",
                        borderRadius: 1.5,
                        border: "1px solid var(--color-border-primary)",
                        maxHeight: '80vh',
                        display: 'flex',
                        flexDirection: 'column'
                    },
                }}
            >
                <DialogTitle
                    sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        p: 2.5,
                        pb: 2,
                        borderBottom: "1px solid var(--color-border-primary)",
                    }}
                >
                    <Box display="flex" alignItems="center" gap={1.5}>
                        <Box
                            sx={{
                                width: 36,
                                height: 36,
                                borderRadius: 1,
                                bgcolor: "var(--color-primary-lighter)",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                            }}
                        >
                            <Plus style={{ color: "var(--color-primary)" }} size={18} />
                        </Box>
                        <Typography fontSize="var(--font-size-base)" fontWeight={600} color="var(--color-text-primary)">
                            Add Symbols
                        </Typography>
                    </Box>
                    <Button
                        onClick={() => {
                            setOpenAddSymbols(false);
                            setSearchQuery("");
                            setSearchResults([]);
                            setSelectedSymbols([]);
                        }}
                        sx={{
                            p: 0.5,
                            minWidth: 'auto',
                            color: 'var(--color-text-secondary)',
                            '&:hover': { bgcolor: 'var(--color-surface-hover)' }
                        }}
                    >
                        <X size={20} />
                    </Button>
                </DialogTitle>

                <DialogContent sx={{ p: 2.5, flex: 1, overflow: 'auto' }}>
                    <Typography fontSize="var(--font-size-base)" color="var(--color-text-secondary)" mb={2} lineHeight={1.6}>
                        Search for stocks to add to your watchlist.
                    </Typography>

                    <Box sx={{ position: 'relative', mb: 2 }}>
                        <input
                            autoFocus
                            type="text"
                            placeholder="Search by ticker or company name..."
                            value={searchQuery}
                            onChange={(e) => handleSearch(e.target.value)}
                            style={{
                                width: '100%',
                                padding: '8px 16px',
                                backgroundColor: 'var(--color-bg-secondary)',
                                border: '1px solid var(--color-border-primary)',
                                color: 'var(--color-text-primary)',
                                borderRadius: '8px',
                                fontSize: '13.5px',
                                fontFamily: 'inherit'
                            }}
                        />
                        {searching && (
                            <Box sx={{ position: 'absolute', right: 12, top: '50%', transform: 'translateY(-50%)' }}>
                                <Box
                                    sx={{
                                        width: 16,
                                        height: 16,
                                        border: 2,
                                        borderColor: 'var(--color-primary)',
                                        borderTopColor: 'transparent',
                                        borderRadius: '50%',
                                        animation: 'spin 0.6s linear infinite'
                                    }}
                                />
                            </Box>
                        )}
                    </Box>

                    {selectedSymbols.length > 0 && (
                        <Box
                            sx={{
                                mb: 2,
                                p: 1.5,
                                bgcolor: 'var(--color-bg-secondary)',
                                borderRadius: 1,
                                border: '1px solid var(--color-border-primary)'
                            }}
                        >
                            <Typography fontSize="var(--font-size-sm)" fontWeight={600} color="var(--color-text-secondary)" mb={1}>
                                Selected ({selectedSymbols.length})
                            </Typography>
                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                                {selectedSymbols.map(symbol => (
                                    <Box
                                        key={symbol}
                                        sx={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: 0.5,
                                            px: 1,
                                            py: 0.5,
                                            bgcolor: 'var(--color-primary-lighter)',
                                            border: '1px solid var(--color-primary)',
                                            borderRadius: 0.5,
                                            color: 'var(--color-primary)',
                                            fontSize: "var(--font-size-sm)"
                                        }}
                                    >
                                        <span>{symbol}</span>
                                        <Button
                                            onClick={() => setSelectedSymbols(prev => prev.filter(s => s !== symbol))}
                                            sx={{
                                                p: 0.25,
                                                minWidth: 'auto',
                                                color: 'inherit',
                                                '&:hover': { bgcolor: 'rgba(0,0,0,0.1)' }
                                            }}
                                        >
                                            <X size={14} />
                                        </Button>
                                    </Box>
                                ))}
                            </Box>
                        </Box>
                    )}

                    {searchQuery && searchResults.length > 0 && (
                        <Box
                            sx={{
                                border: '1px solid var(--color-border-primary)',
                                borderRadius: 1,
                                overflow: 'hidden'
                            }}
                        >
                            {searchResults.map(result => (
                                <Box
                                    key={result.ticker}
                                    onClick={() => {
                                        if (!selectedSymbols.includes(result.ticker)) {
                                            setSelectedSymbols(prev => [...prev, result.ticker]);
                                        }
                                    }}
                                    sx={{
                                        p: 1.5,
                                        borderBottom: '1px solid var(--color-border-primary)',
                                        cursor: 'pointer',
                                        transition: 'background-color 0.15s',
                                        bgcolor: selectedSymbols.includes(result.ticker) ? 'var(--color-primary-lighter)' : 'transparent',
                                        '&:hover': {
                                            bgcolor: selectedSymbols.includes(result.ticker) ? 'var(--color-primary-lighter)' : 'var(--color-surface-hover)'
                                        },
                                        '&:last-child': {
                                            borderBottom: 'none'
                                        }
                                    }}
                                >
                                    <Box display="flex" alignItems="center" justifyContent="space-between">
                                        <Box>
                                            <Typography fontSize="var(--font-size-sm)" fontWeight={600} color="var(--color-text-primary)" mb={0.25}>
                                                {result.ticker}
                                            </Typography>
                                            <Typography fontSize="var(--font-size-sm)" color="var(--color-text-secondary)">
                                                {result.company_name || result.name}
                                            </Typography>
                                        </Box>
                                        {selectedSymbols.includes(result.ticker) && (
                                            <Check size={18} style={{ color: 'var(--color-primary)' }} />
                                        )}
                                    </Box>
                                </Box>
                            ))}
                        </Box>
                    )}

                    {searchQuery && !searching && searchResults.length === 0 && (
                        <Box py={4} textAlign="center">
                            <Typography fontSize="var(--font-size-base)" color="var(--color-text-secondary)">
                                No results found
                            </Typography>
                        </Box>
                    )}

                    {!searchQuery && (
                        <Box py={4} textAlign="center">
                            <Typography fontSize="var(--font-size-base)" color="var(--color-text-secondary)">
                                Start typing to search for symbols
                            </Typography>
                        </Box>
                    )}
                </DialogContent>

                <DialogActions sx={{ gap: 1, p: 2.5, borderTop: "1px solid var(--color-border-primary)" }}>
                    <Button
                        onClick={() => {
                            setOpenAddSymbols(false);
                            setSearchQuery("");
                            setSearchResults([]);
                            setSelectedSymbols([]);
                        }}
                        fullWidth
                        sx={{
                            textTransform: "none",
                            fontSize: "var(--font-size-sm)",
                            fontWeight: 500,
                            color: "var(--color-text-secondary)",
                            "&:hover": { bgcolor: "var(--color-surface-hover)" },
                        }}
                    >
                        Cancel
                    </Button>
                    <Button
                        onClick={handleSaveSymbols}
                        disabled={selectedSymbols.length === 0}
                        fullWidth
                        variant="contained"
                        sx={{
                            textTransform: "none",
                            fontSize: "var(--font-size-sm)",
                            fontWeight: 500,
                            bgcolor: "var(--color-primary)",
                            "&:hover": { bgcolor: "var(--color-primary-dark)" },
                            boxShadow: "none",
                            "&.Mui-disabled": {
                                bgcolor: "var(--color-surface-hover)",
                                color: "var(--color-text-muted)"
                            }
                        }}
                    >
                        Add {selectedSymbols.length > 0 ? `(${selectedSymbols.length})` : 'Symbols'}
                    </Button>
                </DialogActions>
            </Dialog>

            <CreatePortfolioFromWatchlist
                open={openCreateFromWatchlist}
                onClose={() => {
                    setOpenCreateFromWatchlist(false);
                    setSelectedWatchlist(null);
                }}
                watchlist={selectedWatchlist}
                onCreated={(portfolio) => {
                    setOpenCreateFromWatchlist(false);
                    setSelectedWatchlist(null);
                    setSnackbar({ message: `Portfolio "${portfolio.name}" created successfully` });
                }}
            />

            <EfficientFrontierDialog
                open={openEfficientFrontier}
                onClose={() => setOpenEfficientFrontier(false)}
                watchlists={watchlists}
                onNavigate={handleNavigateToFrontier}
            />

            {/* Snackbar */}
            {snackbar && (
                <Box
                    sx={{
                        position: 'fixed',
                        bottom: 24,
                        left: '50%',
                        transform: 'translateX(-50%)',
                        bgcolor: 'var(--color-surface)',
                        border: '1px solid var(--color-border-primary)',
                        color: 'var(--color-text-primary)',
                        px: 2.5,
                        py: 1.25,
                        borderRadius: 1,
                        boxShadow: 'var(--shadow-xl)',
                        fontSize: 15,
                        zIndex: 9999
                    }}
                >
                    {snackbar.message}
                </Box>
            )}

            <style>{`
                @keyframes spin {
                    from { transform: rotate(0deg); }
                    to { transform: rotate(360deg); }
                }
            `}</style>
        </Box>
    );
}